// ---------------------------------------------------------------------------
// Backfill de videos ya subidos (acción admin, client-side). Reprocesa las filas
// tipo='video' SIN poster_url: quita el audio y genera el póster reutilizando el
// pipeline de Fase 2 (stripAudio / generarPoster — regla 10.1, no se duplica nada).
//
// Dos modos:
//  - dryRun: sube a un path TEMPORAL claramente separado (reportes/_backfill_dryrun/…)
//    para inspección; NO toca filas ni objetos existentes.
//  - aplicar: sube a un path NUEVO de producción (sufijo _na.mp4 / _poster.jpg), sin
//    sobrescribir el original, y hace UPDATE de la fila (url + poster_url). Preserva
//    los originales.
// ---------------------------------------------------------------------------
import { client } from "./supabase";
import { stripAudio, generarPoster } from "./videoProcessing";

const BUCKET = "galeria";
// Carpeta TEMPORAL del dry-run — marcada para poder limpiarla después.
export const DRYRUN_PREFIX = "reportes/_backfill_dryrun";

// Filas de video sin póster (candidatas a backfill), más antiguas primero.
export const getVideosSinPoster = async () => {
  const { data, error } = await client
    .from("reportes_fotograficos")
    .select("id, tipo, url, poster_url, idedificio, sale_id, contrato_id, periodo, created_at")
    .eq("tipo", "video")
    .is("poster_url", null)
    .order("id", { ascending: true });
  if (error) throw error;
  return data || [];
};

// Path dentro del bucket galeria a partir de la URL pública.
const storagePathDesdeUrl = (url) => {
  const m = new URL(url).pathname.match(/\/storage\/v1\/object\/public\/galeria\/(.+)/);
  if (!m) throw new Error("URL fuera del bucket galeria: " + url);
  return decodeURIComponent(m[1]);
};

const publicUrl = (path) => client.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;

// fetch del mp4 original + strip de audio + póster. Común a ambos modos.
const procesarBlobs = async (row) => {
  const resp = await fetch(row.url);
  if (!resp.ok) throw new Error(`fetch del original: HTTP ${resp.status}`);
  const original = await resp.blob();
  const sinAudio = await stripAudio(original); // obligatorio: lanza si no puede (fail-closed)
  const poster = await generarPoster(original); // acá best-effort NO: en backfill queremos ambos
  return { original, sinAudio, poster };
};

const subir = async (path, blob, contentType) => {
  const { error } = await client.storage.from(BUCKET).upload(path, blob, { upsert: true, contentType });
  if (error) throw error;
  return publicUrl(path);
};

// DRY-RUN: sube a carpeta temporal separada. NO toca filas ni objetos de producción.
export const backfillDryRun = async (row) => {
  const { original, sinAudio, poster } = await procesarBlobs(row);
  const base = `${DRYRUN_PREFIX}/${row.id}`;
  const urlNuevoVideo = await subir(`${base}_video.mp4`, sinAudio, "video/mp4");
  const urlPoster = await subir(`${base}_poster.jpg`, poster, "image/jpeg");
  return {
    id: row.id,
    idedificio: row.idedificio,
    urlOriginal: row.url,
    urlNuevoVideo,
    urlPoster,
    tamOriginal: original.size,
    tamSinAudio: sinAudio.size,
    tamPoster: poster.size,
  };
};

// APLICAR: sube a path NUEVO de producción (no sobrescribe el original) + UPDATE fila.
export const backfillAplicar = async (row) => {
  const { original, sinAudio, poster } = await procesarBlobs(row);
  const origPath = storagePathDesdeUrl(row.url); // ej. reportes/2287/150_123.mp4
  const videoPath = origPath.replace(/\.[^.]+$/, "_na.mp4"); // NUEVO (no audio), original intacto
  const posterPath = origPath.replace(/\.[^.]+$/, "_poster.jpg");
  const urlNuevoVideo = await subir(videoPath, sinAudio, "video/mp4");
  const urlPoster = await subir(posterPath, poster, "image/jpeg");
  const { error } = await client
    .from("reportes_fotograficos")
    .update({ url: urlNuevoVideo, poster_url: urlPoster })
    .eq("id", row.id);
  if (error) throw error;
  return {
    id: row.id,
    idedificio: row.idedificio,
    urlAnterior: row.url,
    urlNuevoVideo,
    urlPoster,
    tamOriginal: original.size,
    tamSinAudio: sinAudio.size,
    tamPoster: poster.size,
  };
};
